import React, { useMemo } from "react";
import clsx from "clsx";

import { ITeachingMaterial, ILesson } from "../../../state";
import { isOldMaterial } from "../../../libs/teachingMaterialUtils";

import styles from "./PublishMaterialModal.module.scss";

interface IPublishMaterialModalProps {
  material: ITeachingMaterial;
  calling: boolean;
  onPublishLesson: (lesson_id: number) => void;
  onUnPublishLesson: (lesson_id: number) => void;
  onPublishMaterial: (teaching_material_id: number) => void;
  onUnPublishMaterial: (teaching_material_id: number) => void;
  onClose: () => void;
}

const PublishMaterialModal: React.FC<IPublishMaterialModalProps> = ({
  material,
  calling,
  onPublishLesson,
  onUnPublishLesson,
  onPublishMaterial,
  onUnPublishMaterial,
  onClose,
}) => {
  const lessons = useMemo(
    () =>
      (material.lessons || []).filter(
        l =>
          isOldMaterial(material.mitsuwa_id) || !l.title?.includes("はじめに"),
      ),
    [material.lessons, material.mitsuwa_id],
  );

  const headerName = material.name || "";
  const headerTitle = material.title
    ? material.title.replace(/\s*-高校-|\s*-中学-/g, "")
    : "";

  const publishableLessons = lessons.filter(l => !l.teacher_only);
  const allPublished =
    publishableLessons.length > 0 && publishableLessons.every(l => l.published);
  const allUnpublished =
    publishableLessons.length > 0 &&
    publishableLessons.every(l => !l.published);

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.container} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <span className={styles.titleText}>
            <span>{headerName}</span>
            <span>{headerTitle}</span>
          </span>
          <div className={styles.bulkActions}>
            <button
              className={clsx(
                styles.bulkPublishBtn,
                allPublished && styles.active,
              )}
              disabled={calling}
              onClick={() => !allPublished && onPublishMaterial(material.id)}
            >
              一括公開
            </button>
            <button
              className={clsx(
                styles.bulkUnpublishBtn,
                allUnpublished && styles.active,
              )}
              disabled={calling}
              onClick={() =>
                !allUnpublished && onUnPublishMaterial(material.id)
              }
            >
              一括非公開
            </button>
          </div>
        </div>
        <div className={styles.body}>
          {lessons.map((lesson: ILesson) => (
            <div key={lesson.id} className={styles.lessonRow}>
              <div className={styles.textWrapper}>
                <div
                  className={clsx(
                    styles.lessonInfo,
                    !lesson.published && styles.lessonInfoUnpublished,
                  )}
                >
                  {lesson.name?.trim() && (
                    <div className={styles.lessonName}>{lesson.name}</div>
                  )}
                  {lesson.title && (
                    <div className={styles.lessonTitle}>{lesson.title}</div>
                  )}
                </div>
                {!lesson.published && !lesson.teacher_only && (
                  <div className={styles.unpublishedBadge}>
                    生徒に未公開です
                  </div>
                )}
              </div>
              {!lesson.teacher_only && (
                <div className={styles.publishControls}>
                  <button
                    className={clsx(
                      styles.publishBtn,
                      lesson.published && styles.active,
                    )}
                    onClick={() =>
                      !lesson.published && onPublishLesson(lesson.id)
                    }
                    disabled={calling}
                  >
                    公開する
                  </button>
                  <button
                    className={clsx(
                      styles.unpublishBtn,
                      !lesson.published && styles.active,
                    )}
                    onClick={() =>
                      lesson.published && onUnPublishLesson(lesson.id)
                    }
                    disabled={calling}
                  >
                    公開しない
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PublishMaterialModal;
